import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import './App.css';

// Eagerly load login/register (needed immediately)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';

// Lazily load everything else
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const EquitiesPage = lazy(() => import('./pages/EquitiesPage'));
const BondsPage = lazy(() => import('./pages/BondsPage'));
const TreasuryBondsPage = lazy(() => import('./pages/TreasuryBondsPage'));
const GICsPage = lazy(() => import('./pages/GICsPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderBondsPage = lazy(() => import('./pages/OrderBondsPage'));
const OrderEquitiesPage = lazy(() => import('./pages/OrderEquitiesPage'));
const OrderTreasuryBondsPage = lazy(() => import('./pages/OrderTreasuryBondsPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const DepositListPage = lazy(() => import('./pages/DepositListPage'));
const AddDepositPage = lazy(() => import('./pages/AddDepositPage'));
const WithdrawalListPage = lazy(() => import('./pages/WithdrawalListPage'));
const AddWithdrawalPage = lazy(() => import('./pages/AddWithdrawalPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientEditPage = lazy(() => import('./pages/ClientEditPage'));
const BankDetailsListPage = lazy(() => import('./pages/BankDetailsListPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const PageFallback = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <Layout>
        <PageFallback />
      </Layout>
    );
  }
  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const PrivateAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <Layout>
        <PageFallback />
      </Layout>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.is_admin) return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageFallback />;
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

        {/* Client + Admin */}
        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/portfolio" element={<PrivateRoute><PortfolioPage /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

        {/* Products */}
        <Route path="/bonds" element={<PrivateRoute><BondsPage /></PrivateRoute>} />
        <Route path="/treasury-bonds" element={<PrivateRoute><TreasuryBondsPage /></PrivateRoute>} />
        <Route path="/equities" element={<PrivateRoute><EquitiesPage /></PrivateRoute>} />
        <Route path="/gics" element={<PrivateRoute><GICsPage /></PrivateRoute>} />

        {/* Orders */}
        <Route path="/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
        <Route path="/order-bonds" element={<PrivateRoute><OrderBondsPage /></PrivateRoute>} />
        <Route path="/order-equities" element={<PrivateRoute><OrderEquitiesPage /></PrivateRoute>} />
        <Route path="/order-treasury-bonds" element={<PrivateRoute><OrderTreasuryBondsPage /></PrivateRoute>} />

        {/* Transactions */}
        <Route path="/transactions" element={<PrivateRoute><TransactionsPage /></PrivateRoute>} />
        <Route path="/deposits" element={<PrivateRoute><DepositListPage /></PrivateRoute>} />
        <Route path="/deposits/new" element={<PrivateRoute><AddDepositPage /></PrivateRoute>} />
        <Route path="/withdrawals" element={<PrivateRoute><WithdrawalListPage /></PrivateRoute>} />
        <Route path="/withdrawals/new" element={<PrivateRoute><AddWithdrawalPage /></PrivateRoute>} />

        {/* Admin only */}
        <Route path="/admin" element={<PrivateAdminRoute><AdminDashboardPage /></PrivateAdminRoute>} />
        <Route path="/clients" element={<PrivateAdminRoute><ClientsPage /></PrivateAdminRoute>} />
        <Route path="/clients/new" element={<PrivateAdminRoute><ClientEditPage /></PrivateAdminRoute>} />
        <Route path="/clients/:id/edit" element={<PrivateAdminRoute><ClientEditPage /></PrivateAdminRoute>} />
        <Route path="/bank-details-list" element={<PrivateAdminRoute><BankDetailsListPage /></PrivateAdminRoute>} />

        {/* Default */}
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
